import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from 'react-query';
import { getAllUsers } from '../services/userApi';
import { getQuizResultsReport } from '../services/quizApi';
import { getAllFeedbacks } from '../services/feedbackApi';
import { useTranslation } from 'react-i18next'; // Keep this import
import { Users, BookOpen, MessageSquare, TrendingUp } from 'lucide-react';
import * as XLSX from 'xlsx';
import AdminNav from '../components/AdminNav';

// Component for Pagination Controls
const Pagination = ({ currentPage, totalPages, onPageChange }) => {
  const { t } = useTranslation(); // Get t function here

  if (totalPages <= 1) return null;

  return (
    <div className="pagination">
      <button onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>
        {t('common.previous', 'Previous')}
      </button>
      <span>
        Page {currentPage} of {totalPages}
      </span>
      <button onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>
        {t('common.next', 'Next')}
      </button>
    </div>
  );
};

const AdminDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Fetch all users
  const { data: users, isLoading: isLoadingUsers, error: usersError } = useQuery(
    'adminUsers',
    getAllUsers
  );

  // Fetch quiz results report
  const { data: quizReport, isLoading: isLoadingQuizReport, error: quizReportError } = useQuery(
    'adminQuizReport',
    () => getQuizResultsReport() // No specific userId for overall report
  );

  // Fetch all feedbacks
  const { data: feedbacks, isLoading: isLoadingFeedbacks, error: feedbacksError } = useQuery(
    'adminFeedbacks',
    getAllFeedbacks
  );

  // State for user table pagination
  const [userCurrentPage, setUserCurrentPage] = useState(1);
  const usersPerPage = 10;

  // Pagination logic for users
  const indexOfLastUser = userCurrentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = users ? users.slice(indexOfFirstUser, indexOfLastUser) : [];
  const totalUserPages = users ? Math.ceil(users.length / usersPerPage) : 0;

  const totalUsers = users ? users.length : 0;
  const adminUsers = users ? users.filter(user => user.role === 'admin').length : 0;
  const normalUsers = totalUsers - adminUsers;

  const totalFeedbacks = feedbacks ? feedbacks.length : 0;

  const averageQuizScore = quizReport && quizReport.length > 0
    ? (quizReport.reduce((sum, r) => sum + r.averagePercentage, 0) / quizReport.length).toFixed(2)
    : 'N/A';

  const totalQuizzesTaken = quizReport
    ? quizReport.reduce((sum, r) => sum + r.totalQuizzes, 0)
    : 0;

  const exportUsersToExcel = () => {
    if (!users) return;
    const ws = XLSX.utils.json_to_sheet(users.map(user => ({
      'Full Name': user.fullName,
      'Email': user.email,
      'Phone': user.phone,
      'Country': user.country,
      'Gender': user.gender || 'N/A',
      'Language': user.preferredLanguage,
      'Role': user.role,
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Users');
    XLSX.writeFile(wb, 'users.xlsx');
  };

  const exportFeedbacksToExcel = () => {
    if (!feedbacks) return;
    const ws = XLSX.utils.json_to_sheet(feedbacks.map(feedback => ({
      'User': feedback.userId,
      'Rating': feedback.rating,
      'Comment': feedback.comment,
      'Date': new Date(feedback.createdAt).toLocaleDateString(),
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Feedbacks');
    XLSX.writeFile(wb, 'feedbacks.xlsx');
  };

  const exportQuizSummaryToExcel = () => {
    if (!quizReport) return;
    const ws = XLSX.utils.json_to_sheet(quizReport.map(report => ({
      'User ID': report.userId,
      'Total Quizzes': report.totalQuizzes,
      'Avg. %': report.averagePercentage.toFixed(2) + '%',
      'Total Points': report.totalPoints,
      'Last Taken': new Date(report.lastTakenAt).toLocaleDateString(),
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Quiz Summary');
    XLSX.writeFile(wb, 'quiz_summary.xlsx');
  };

  const renderCard = (title, value, icon, loading, error) => (
    <div className="admin-card" style={{padding: 10}}>
      <div className="admin-card-icon">{icon}</div>
      <div className="admin-card-content">
        <h3>{title}</h3>
        {loading ? (
          <p>Loading...</p>
        ) : error ? (
          <p className="text-red-500">Error</p>
        ) : (
          <p className="admin-card-value">{value}</p>
        )}
      </div>
    </div>
  );

  return (
    <div 
    // className="admin-dashboard-container"
     className="module-details-page feedback-page"
    >
    <AdminNav />
    
      <h1 className="admin-dashboard-title">{t('admin.dashboard.title', 'Admin Dashboard')}</h1>

      <div style={{ padding: '0 10px 20px 10px' }}>
        <button className="btn-primary" onClick={() => navigate('/admin/certificate')}>
          Manual Certificate Generation
        </button>
      </div>

      <div className="admin-stats-grid" >
        {renderCard(
          t('admin.dashboard.totalUsers', 'Total Users'),
          totalUsers,
          <Users size={32} />,
          isLoadingUsers,
          usersError
        )}
        {renderCard(
          t('admin.dashboard.adminUsers', 'Admin Users'),
          adminUsers,
          <Users size={32} />,
          isLoadingUsers,
          usersError
        )}
        {renderCard(
          t('admin.dashboard.normalUsers', 'Normal Users'),
          normalUsers,
          <Users size={32} />,
          isLoadingUsers,
          usersError
        )}
        {renderCard(
          t('admin.dashboard.totalFeedbacks', 'Total Feedbacks'),
          totalFeedbacks,
          <MessageSquare size={32} />,
          isLoadingFeedbacks,
          feedbacksError
        )}
        {renderCard(
          t('admin.dashboard.avgQuizScore', 'Avg. Quiz Score'),
          `${averageQuizScore}%`,
          <TrendingUp size={32} />,
          isLoadingQuizReport,
          quizReportError
        )}
        {renderCard(
          t('admin.dashboard.totalQuizzesTaken', 'Total Quizzes Taken'),
          totalQuizzesTaken,
          <BookOpen size={32} />,
          isLoadingQuizReport,
          quizReportError
        )}
      </div>

      <div className="admin-section" style={{padding: 10}}>
        <h2>{t('admin.dashboard.latestFeedbacks', 'Latest Feedbacks')}</h2>
        {isLoadingFeedbacks ? (
          <p>Loading feedbacks...</p>
        ) : feedbacksError ? (
          <p className="text-red-500">Error loading feedbacks: {feedbacksError.message}</p>
        ) : (
          <>
            <div className="admin-table-controls">
              <button onClick={exportFeedbacksToExcel}>Export to Excel</button>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('admin.feedback.user', 'User')}</th>
                    <th>{t('admin.feedback.rating', 'Rating')}</th>
                    <th>{t('admin.feedback.comment', 'Comment')}</th>
                    <th>{t('admin.feedback.date', 'Date')}</th>
                  </tr>
                </thead>
                <tbody>
                  {feedbacks && feedbacks.length > 0 ? (
                    feedbacks.slice(0, 5).map((feedback) => ( // Show top 5 latest feedbacks
                      <tr key={feedback.feedbackId}>
                        <td>{feedback.userId}</td>
                        <td>{feedback.rating}</td>
                        <td>{feedback.comment}</td>
                        <td>{new Date(feedback.createdAt).toLocaleDateString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4">{t('admin.feedback.noFeedbacks', 'No feedbacks available.')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <div className="admin-section" style={{padding: 10}}>
        <h2>{t('admin.dashboard.allUsers', 'All Users')}</h2>
        {isLoadingUsers ? (
          <p>Loading users...</p>
        ) : usersError ? (
          <p className="text-red-500">Error loading users: {usersError.message}</p>
        ) : (
          <>
            <div className="admin-table-controls">
              <button onClick={exportUsersToExcel}>Export to Excel</button>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('admin.users.fullName', 'Full Name')}</th>
                    <th>{t('admin.users.email', 'Email')}</th>
                    <th>{t('admin.users.phone', 'Phone')}</th>
                    <th>{t('admin.users.country', 'Country')}</th>
                    <th>{t('admin.users.gender', 'Gender')}</th>
                    <th>{t('admin.users.language', 'Language')}</th>
                    <th>{t('admin.users.role', 'Role')}</th>
                  </tr>
                </thead>
                <tbody>
                  {currentUsers.length > 0 ? (
                    currentUsers.map((user) => (
                      <tr key={user.userId}>
                        <td>{user.fullName}</td>
                        <td>{user.email}</td>
                        <td>{user.phone}</td>
                        <td>{user.country}</td>
                        <td>{user.gender || 'N/A'}</td>
                        <td>{user.preferredLanguage}</td>
                        <td>{user.role}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7">{t('admin.users.noUsers', 'No users available.')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <Pagination
              currentPage={userCurrentPage}
              totalPages={totalUserPages}
              onPageChange={setUserCurrentPage}
            />
          </>
        )}
      </div>

      <div className="admin-section" style={{padding: 10}}>
        <h2>{t('admin.dashboard.quizSummary', 'Quiz Summary by User')}</h2>
        {isLoadingQuizReport ? (
          <p>Loading quiz report...</p>
        ) : quizReportError ? (
          <p className="text-red-500">Error loading quiz report: {quizReportError.message}</p>
        ) : (
          <>
            <div className="admin-table-controls">
              <button onClick={exportQuizSummaryToExcel}>Export to Excel</button>
            </div>
            <div className="admin-table-container">
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>{t('admin.quiz.userId', 'User ID')}</th>
                    <th>{t('admin.quiz.totalQuizzes', 'Total Quizzes')}</th>
                    <th>{t('admin.quiz.avgPercentage', 'Avg. %')}</th>
                    <th>{t('admin.quiz.totalPoints', 'Total Points')}</th>
                    <th>{t('admin.quiz.lastTaken', 'Last Taken')}</th>
                  </tr>
                </thead>
                <tbody>
                  {quizReport && quizReport.length > 0 ? (
                    quizReport.map((report) => (
                      <tr key={report.userId}>
                        <td>{report.userId}</td>
                        <td>{report.totalQuizzes}</td>
                        <td>{report.averagePercentage.toFixed(2)}%</td>
                        <td>{report.totalPoints}</td>
                        <td>{new Date(report.lastTakenAt).toLocaleDateString()}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5">{t('admin.quiz.noQuizData', 'No quiz data available.')}</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboardPage;